import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { contents } from "@/mocks/contents";
import { searchContents } from "@/utils/content";
import { usePageMeta } from "@/hooks/usePageMeta";
import SearchExplorer from "./components/SearchExplorer";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const urlKeyword = params.get("q") ?? "";
  const [input, setInput] = useState(urlKeyword);

  useEffect(() => {
    setInput(urlKeyword);
  }, [urlKeyword]);

  const results = searchContents(urlKeyword);
  const hotItems = [...contents].sort((a, b) => b.views - a.views);

  usePageMeta(
    urlKeyword ? `「${urlKeyword}」搜索结果 - 国语对白做受69` : "搜索影音资源 - 国语对白做受69",
    urlKeyword
      ? `国语对白做受69站内关于「${urlKeyword}」的影音资源搜索结果，支持按地区、年份、类型筛选与排序。`
      : "在国语对白做受69站内搜索影视影音作品，快速查找你感兴趣的国语对白资源。",
    `搜索,${urlKeyword},国语对白做受69,国语对白,影音资源,影视作品`,
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (q) setParams({ q });
    else setParams({});
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteNavbar />

      <main className="flex-1 w-full">
        <section className="w-full bg-primary-950">
          <div className="mx-auto max-w-page px-4 md:px-8 py-8 md:py-12">
            <div className="text-background-300">
              <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
            </div>
            <h1 className="mt-4 text-xl md:text-2xl font-bold text-background-50 font-heading">
              搜索影音资源
            </h1>
            <form onSubmit={handleSubmit} className="mt-4 flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="输入作品标题、主创或标签关键词"
                  className="w-full rounded-full border border-background-50/20 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-accent-400"
                />
              </div>
              <button
                type="submit"
                className="rounded-full bg-accent-500 px-7 py-3 text-sm font-medium text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>
            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-background-300">
              <span>热门搜索：</span>
              {["国语对白", "悬疑", "纪录片", "治愈", "高分"].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setParams({ q: tag })}
                  className="rounded-full border border-background-50/25 px-3 py-1 hover:bg-background-50/10 transition-colors cursor-pointer"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-page px-4 md:px-8 py-8 md:py-12">
          {urlKeyword ? (
            <>
              <p className="mb-6 text-sm text-foreground-600">
                关于 <span className="font-semibold text-foreground-900">「{urlKeyword}」</span> 共找到{" "}
                <span className="font-semibold text-accent-600">{results.length}</span> 部相关资源
              </p>
              <SearchExplorer key={urlKeyword} baseItems={results} keyword={urlKeyword} hotItems={hotItems} />
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100">
                <i className="ri-search-eye-line text-3xl text-foreground-400" />
              </span>
              <h2 className="mt-4 text-base font-semibold text-foreground-800">输入关键词开始搜索</h2>
              <p className="mt-2 text-sm text-foreground-500">
                支持按作品标题、简介、主创、类型与标签检索，也可以直接浏览下方热门资源。
              </p>
              <div className="mt-8 text-left">
                <h3 className="text-base font-bold text-foreground-950 font-heading flex items-center gap-2 mb-5">
                  <span className="w-1 h-5 rounded-full bg-accent-500" />
                  热门推荐
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                  {hotItems.slice(0, 10).map((item, i) => (
                    <div key={item.id} className="text-left">
                      <a
                        href={`/detail/${item.id}`}
                        className="block cursor-pointer"
                      >
                        <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                          <img
                            src={item.cover}
                            alt={`${item.title} 热门国语影音资源`}
                            title={`${item.title} 国语对白高清影音`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <p className="mt-2 text-sm font-semibold text-foreground-950 truncate">{item.title}</p>
                        <p className="mt-1 text-xs text-foreground-500">{item.region} · {item.year}</p>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}